'use client';
import React, { useState } from 'react';
import { useForm } from 'react-hook-form';
import { CreditCard, Smartphone, Building2, CheckCircle, Shield } from 'lucide-react';
import LRAModal from '@/components/ui/LRAModal';

interface QuickPayFormData {
  channel: string;
  phoneNumber?: string;
  accountNumber?: string;
  confirmAmount: boolean;
}

type PayChannel = 'orange-money' | 'mtn-money' | 'bank-transfer' | 'ecobank';

interface QuickPayModalProps {
  open: boolean;
  onClose: () => void;
  obligationId: string | null;
}

// Backend integration point: replace with API call to /api/payments/initiate
const obligationDetails: Record<string, { type: string; amount: number; period: string }> = {
  'obl-001': { type: 'Goods & Services Tax (GST)', amount: 2140.0, period: 'Q1 2026' },
  'obl-002': { type: 'Corporate Income Tax', amount: 2680.0, period: 'FY 2025 Annual' },
  'obl-003': { type: 'Property Tax', amount: 780.0, period: 'FY 2026' },
};

const channels: { id: PayChannel; label: string; icon: React.ReactNode; color: string; note: string }[] = [
  { id: 'orange-money', label: 'Orange Money', icon: <Smartphone size={16} />, color: '#ff6b00', note: 'Instant · 24/7' },
  { id: 'mtn-money', label: 'MTN MoMo', icon: <Smartphone size={16} />, color: '#ffcc00', note: 'Instant · 24/7' },
  { id: 'bank-transfer', label: 'Bank Transfer', icon: <Building2 size={16} />, color: '#60a5fa', note: '1–2 business days' },
  { id: 'ecobank', label: 'Ecobank Liberia', icon: <CreditCard size={16} />, color: '#34d399', note: 'Same day' },
];

export default function QuickPayModal({ open, onClose, obligationId }: QuickPayModalProps) {
  const [step, setStep] = useState<'select' | 'details' | 'confirm' | 'success'>('select');
  const [selectedChannel, setSelectedChannel] = useState<PayChannel | null>(null);
  const [isProcessing, setIsProcessing] = useState(false);

  const { register, handleSubmit, formState: { errors }, reset } = useForm<QuickPayFormData>();

  const obligation = obligationId ? obligationDetails[obligationId] : null;

  const handleClose = () => {
    onClose();
    setTimeout(() => {
      setStep('select');
      setSelectedChannel(null);
      reset();
    }, 300);
  };

  const handleChannelSelect = (channel: PayChannel) => {
    setSelectedChannel(channel);
    setStep('details');
  };

  const onSubmitPayment = () => {
    setIsProcessing(true);
    setStep('confirm');
    // Backend integration point: POST /api/payments/initiate with { obligationId, channel, amount }
    setTimeout(() => {
      setIsProcessing(false);
      setStep('success');
    }, 2000);
  };

  const channelInfo = channels.find((c) => c.id === selectedChannel);

  return (
    <LRAModal
      open={open}
      onClose={handleClose}
      title={step === 'success' ? 'Payment Successful' : 'Pay Tax Obligation'}
      size="md"
      dark
    >
      {step === 'select' && (
        <div>
          {obligation && (
            <div
              className="rounded-lg p-3 mb-4"
              style={{ backgroundColor: 'rgba(212,160,23,0.08)', border: '1px solid rgba(212,160,23,0.2)' }}
            >
              <p className="text-xs" style={{ color: 'var(--dark-muted)' }}>Paying for</p>
              <p className="text-sm font-semibold" style={{ color: 'var(--dark-text)' }}>
                {obligation.type} · {obligation.period}
              </p>
              <p className="text-xl font-bold tabular-nums mt-1" style={{ color: 'var(--accent)' }}>
                ${obligation.amount.toLocaleString('en-US', { minimumFractionDigits: 2 })}
              </p>
              <p className="text-xs" style={{ color: 'var(--dark-muted)' }}>
                LRD {(obligation.amount * 160).toLocaleString()} equivalent
              </p>
            </div>
          )}

          <p className="text-xs font-semibold uppercase tracking-wide mb-3" style={{ color: 'var(--dark-muted)' }}>
            Select Payment Channel
          </p>
          <div className="grid grid-cols-2 gap-2">
            {channels.map((ch) => (
              <button
                key={`channel-${ch.id}`}
                onClick={() => handleChannelSelect(ch.id)}
                className="flex flex-col items-start gap-2 p-3 rounded-lg text-left transition-all duration-150 hover:brightness-110 active:scale-95"
                style={{
                  backgroundColor: `${ch.color}15`,
                  border: `1px solid ${ch.color}30`,
                }}
              >
                <div className="flex items-center gap-2">
                  <span style={{ color: ch.color }}>{ch.icon}</span>
                  <span className="text-sm font-semibold" style={{ color: 'var(--dark-text)' }}>
                    {ch.label}
                  </span>
                </div>
                <span className="text-xs" style={{ color: 'var(--dark-muted)' }}>
                  {ch.note}
                </span>
              </button>
            ))}
          </div>

          <div
            className="flex items-center gap-2 mt-4 p-2.5 rounded-lg text-xs"
            style={{ backgroundColor: 'rgba(74,222,128,0.06)', border: '1px solid rgba(74,222,128,0.15)' }}
          >
            <Shield size={13} style={{ color: '#4ade80' }} />
            <span style={{ color: 'var(--dark-muted)' }}>
              All payments settle directly to the LRA Treasury — no collector wallets
            </span>
          </div>
        </div>
      )}

      {step === 'details' && channelInfo && (
        <form onSubmit={handleSubmit(onSubmitPayment)} className="space-y-4">
          <div
            className="flex items-center gap-2 p-3 rounded-lg mb-2"
            style={{ backgroundColor: `${channelInfo.color}15`, border: `1px solid ${channelInfo.color}30` }}
          >
            <span style={{ color: channelInfo.color }}>{channelInfo.icon}</span>
            <span className="text-sm font-semibold" style={{ color: 'var(--dark-text)' }}>
              {channelInfo.label}
            </span>
          </div>

          {(selectedChannel === 'orange-money' || selectedChannel === 'mtn-money') && (
            <div>
              <label className="block text-xs font-semibold mb-1.5 uppercase tracking-wide" style={{ color: 'var(--dark-muted)' }}>
                Mobile Number
              </label>
              <p className="text-xs mb-1.5" style={{ color: 'var(--dark-muted)' }}>
                Enter the mobile money number to debit for this payment
              </p>
              <input
                type="tel"
                placeholder="+231 XX XXX XXXX"
                className="w-full px-3 py-2.5 rounded-lg text-sm outline-none"
                style={{
                  backgroundColor: 'var(--dark-elevated)',
                  border: `1px solid ${errors.phoneNumber ? 'var(--danger-text)' : 'var(--dark-border)'}`,
                  color: 'var(--dark-text)',
                }}
                {...register('phoneNumber', {
                  required: 'Mobile number is required',
                  pattern: { value: /^\+?[\d\s]{10,15}$/, message: 'Enter a valid Liberian phone number' },
                })}
              />
              {errors.phoneNumber && (
                <p className="text-xs mt-1" style={{ color: 'var(--danger-text)' }}>
                  {errors.phoneNumber.message}
                </p>
              )}
            </div>
          )}

          {(selectedChannel === 'bank-transfer' || selectedChannel === 'ecobank') && (
            <div>
              <label className="block text-xs font-semibold mb-1.5 uppercase tracking-wide" style={{ color: 'var(--dark-muted)' }}>
                Account Number
              </label>
              <p className="text-xs mb-1.5" style={{ color: 'var(--dark-muted)' }}>
                Your bank account to debit — must be registered in your taxpayer profile
              </p>
              <input
                type="text"
                placeholder="XXXX XXXX XXXX XXXX"
                className="w-full px-3 py-2.5 rounded-lg text-sm outline-none font-mono-data"
                style={{
                  backgroundColor: 'var(--dark-elevated)',
                  border: `1px solid ${errors.accountNumber ? 'var(--danger-text)' : 'var(--dark-border)'}`,
                  color: 'var(--dark-text)',
                }}
                {...register('accountNumber', {
                  required: 'Account number is required',
                })}
              />
              {errors.accountNumber && (
                <p className="text-xs mt-1" style={{ color: 'var(--danger-text)' }}>
                  {errors.accountNumber.message}
                </p>
              )}
            </div>
          )}

          {obligation && (
            <div
              className="rounded-lg p-3"
              style={{ backgroundColor: 'rgba(255,255,255,0.03)', border: '1px solid var(--dark-border)' }}
            >
              <div className="flex justify-between text-xs mb-1">
                <span style={{ color: 'var(--dark-muted)' }}>Obligation amount</span>
                <span className="tabular-nums font-semibold" style={{ color: 'var(--dark-text)' }}>
                  ${obligation.amount.toLocaleString('en-US', { minimumFractionDigits: 2 })}
                </span>
              </div>
              <div className="flex justify-between text-xs mb-1">
                <span style={{ color: 'var(--dark-muted)' }}>Processing fee</span>
                <span className="tabular-nums" style={{ color: 'var(--dark-muted)' }}>$0.00</span>
              </div>
              <div
                className="flex justify-between text-sm font-bold pt-2 mt-1"
                style={{ borderTop: '1px solid var(--dark-border)', color: 'var(--dark-text)' }}
              >
                <span>Total due</span>
                <span className="tabular-nums" style={{ color: 'var(--accent)' }}>
                  ${obligation.amount.toLocaleString('en-US', { minimumFractionDigits: 2 })}
                </span>
              </div>
            </div>
          )}

          <div className="flex items-start gap-2">
            <input
              type="checkbox"
              id="confirmAmount"
              className="mt-0.5 w-4 h-4 rounded"
              style={{ accentColor: 'var(--accent)' }}
              {...register('confirmAmount', { required: 'You must confirm the payment amount' })}
            />
            <label htmlFor="confirmAmount" className="text-xs" style={{ color: 'var(--dark-muted)' }}>
              I confirm this payment of{' '}
              <strong style={{ color: 'var(--dark-text)' }}>
                ${obligation?.amount.toLocaleString('en-US', { minimumFractionDigits: 2 })}
              </strong>{' '}
              will be deducted from my account and settled directly to the LRA Treasury
            </label>
          </div>
          {errors.confirmAmount && (
            <p className="text-xs" style={{ color: 'var(--danger-text)' }}>
              {errors.confirmAmount.message}
            </p>
          )}

          <div className="flex gap-2 pt-2">
            <button
              type="button"
              onClick={() => setStep('select')}
              className="flex-1 py-2.5 rounded-lg text-sm font-medium transition-all hover:bg-white/10"
              style={{ border: '1px solid var(--dark-border)', color: 'var(--dark-muted)' }}
            >
              Back
            </button>
            <button
              type="submit"
              className="flex-1 py-2.5 rounded-lg text-sm font-semibold transition-all active:scale-95 hover:opacity-90"
              style={{ backgroundColor: 'var(--accent)', color: 'var(--accent-foreground)' }}
            >
              Confirm Payment
            </button>
          </div>
        </form>
      )}

      {step === 'confirm' && (
        <div className="flex flex-col items-center py-6 gap-4">
          <div
            className="w-14 h-14 rounded-full flex items-center justify-center"
            style={{ backgroundColor: 'rgba(212,160,23,0.15)' }}
          >
            <svg className="animate-spin w-7 h-7" viewBox="0 0 24 24" fill="none">
              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="var(--accent)" strokeWidth="4" />
              <path className="opacity-75" fill="var(--accent)" d="M4 12a8 8 0 018-8v8z" />
            </svg>
          </div>
          <div className="text-center">
            <p className="text-sm font-semibold" style={{ color: 'var(--dark-text)' }}>
              Processing Payment
            </p>
            <p className="text-xs mt-1" style={{ color: 'var(--dark-muted)' }}>
              Routing to LRA Treasury · Do not close this window
            </p>
          </div>
        </div>
      )}

      {step === 'success' && (
        <div className="flex flex-col items-center py-4 gap-4">
          <div
            className="w-16 h-16 rounded-full flex items-center justify-center"
            style={{ backgroundColor: 'rgba(74,222,128,0.12)', border: '2px solid rgba(74,222,128,0.3)' }}
          >
            <CheckCircle size={32} style={{ color: '#4ade80' }} />
          </div>
          <div className="text-center">
            <p className="text-lg font-bold" style={{ color: '#4ade80' }}>
              Payment Confirmed
            </p>
            <p className="text-xs mt-1" style={{ color: 'var(--dark-muted)' }}>
              Your payment has been received and reconciled with the LRA Treasury
            </p>
          </div>

          <div
            className="w-full rounded-lg p-3 space-y-2 text-xs"
            style={{ backgroundColor: 'rgba(255,255,255,0.03)', border: '1px solid var(--dark-border)' }}
          >
            {[
              { label: 'Receipt No.', value: `LRA-2026-${Math.floor(100000 + 91234)}` },
              { label: 'Amount Paid', value: `$${obligation?.amount.toLocaleString('en-US', { minimumFractionDigits: 2 })}` },
              { label: 'Channel', value: channelInfo?.label ?? '—' },
              { label: 'Timestamp', value: 'May 28, 2026 · 01:23 AM WAT' },
              { label: 'Treasury Ref.', value: 'GOL-TRY-2026-441829' },
            ].map((row) => (
              <div key={`success-${row.label}`} className="flex justify-between">
                <span style={{ color: 'var(--dark-muted)' }}>{row.label}</span>
                <span className="font-semibold font-mono-data" style={{ color: 'var(--dark-text)' }}>
                  {row.value}
                </span>
              </div>
            ))}
          </div>

          <div className="flex gap-2 w-full">
            <button
              className="flex-1 py-2 rounded-lg text-xs font-medium transition-all hover:bg-white/10"
              style={{ border: '1px solid var(--dark-border)', color: 'var(--dark-muted)' }}
            >
              Download Receipt
            </button>
            <button
              onClick={handleClose}
              className="flex-1 py-2 rounded-lg text-xs font-semibold transition-all hover:opacity-90"
              style={{ backgroundColor: 'var(--accent)', color: 'var(--accent-foreground)' }}
            >
              Done
            </button>
          </div>
        </div>
      )}
    </LRAModal>
  );
}